import httpClient from "../core/httpClient";
import { getAuthEmail } from "../auth/authStorage";
import { apiDateTimeToDatetimeLocal } from "./voyageDaysApi";

const VOYAGES_LIST_CACHE_PREFIX = "voyages.my.list.";

/** Równoległe wywołania listVoyages (np. React StrictMode) — jeden request HTTP. */
const inflightByKey = new Map();

function getLocalStorage() {
	if (typeof window === "undefined") return null;
	try {
		return window.localStorage;
	} catch {
		return null;
	}
}

function listCacheKey(page, limit) {
	const userScope = getAuthEmail() || "_anonymous_";
	return `${VOYAGES_LIST_CACHE_PREFIX}${userScope}.${page}.${limit}`;
}

function readCachedList(key) {
	const storage = getLocalStorage();
	if (!storage) return null;
	try {
		const raw = storage.getItem(key);
		return raw ? JSON.parse(raw) : null;
	} catch {
		return null;
	}
}

function writeCachedList(key, body) {
	const storage = getLocalStorage();
	if (!storage) return;
	try {
		if (body == null) {
			storage.removeItem(key);
		} else {
			storage.setItem(key, JSON.stringify(body));
		}
	} catch {
		// quota / tryb prywatny
	}
}

export function clearCachedVoyagesList() {
	const storage = getLocalStorage();
	if (!storage) return;
	const keys = [];
	for (let i = 0; i < storage.length; i += 1) {
		const key = storage.key(i);
		if (key?.startsWith(VOYAGES_LIST_CACHE_PREFIX)) keys.push(key);
	}
	keys.forEach((key) => storage.removeItem(key));
	inflightByKey.clear();
}

/** Odczyt ostatnio zapisanej listy (bez requestu HTTP) — do natychmiastowego podglądu. */
export function getCachedVoyagesList({ page = 1, limit = 10 } = {}) {
	return readCachedList(listCacheKey(page, limit));
}

async function fetchVoyagesList(page, limit) {
	const response = await httpClient.get("/api/v2/voyages/my", {
		params: { page, limit },
	});
	const body = response.data;
	writeCachedList(listCacheKey(page, limit), body);
	console.info("[voyagesApi] listVoyages: pobrano", body?.data?.length ?? 0, "rejsów", {
		total: body?.meta?.total,
		page: body?.meta?.page,
	});
	return body;
}

/**
 * GET `/api/v2/voyages/my` — rejsy zalogowanego użytkownika.
 * @param {{ page?: number, limit?: number, force?: boolean }} params
 *   force — pomiń cache i pobierz z serwera (np. po zmianie na innym urządzeniu).
 *   Bez force zwraca ostatnią zapisaną kopię z tej przeglądarki (szybszy powrót na stronę).
 * @returns {Promise<{ data: VoyageDto[], meta: { total, page, limit, pages } }>}
 */
export async function listVoyages({ page = 1, limit = 10, force = false } = {}) {
	const key = listCacheKey(page, limit);

	if (!force) {
		const cached = readCachedList(key);
		if (cached) return cached;
		const inflight = inflightByKey.get(key);
		if (inflight) return inflight;
	}

	const promise = fetchVoyagesList(page, limit).finally(() => {
		if (inflightByKey.get(key) === promise) {
			inflightByKey.delete(key);
		}
	});

	if (!force) {
		inflightByKey.set(key, promise);
	}
	return promise;
}

/**
 * GET `/api/v2/voyages/{id}` — szczegóły rejsu po id.
 * @param {number|string} voyageId
 * @returns {Promise<VoyageDto|null>}
 */
export async function findVoyageById(voyageId) {
	try {
		const response = await httpClient.get(`/api/v2/voyages/${Number(voyageId)}`);
		const body = response.data;
		console.info('[voyagesApi] findVoyageById: pobrano rejs', { id: body?.id, name: body?.name });
		return body ?? null;
	} catch (error) {
		if (error?.response?.status === 404) return null;
		throw error;
	}
}

function emptyToNull(value) {
	if (value == null) return null;
	const s = String(value).trim();
	return s === "" ? null : s;
}

/** Wartość z `datetime-local` → `YYYY-MM-DD HH:mm:ss` lub null. */
function datetimeLocalToApi(value) {
	const s = emptyToNull(value);
	if (s == null) return null;
	const d = new Date(s);
	if (Number.isNaN(d.getTime())) return null;
	const pad = (n) => String(n).padStart(2, "0");
	return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}:00`;
}

function buildVoyageBody(payload) {
	return {
		name: emptyToNull(payload.name),
		check_in_at: datetimeLocalToApi(payload.check_in_at),
		check_out_at: datetimeLocalToApi(payload.check_out_at),
	};
}

/**
 * POST `/api/v2/voyages`
 * @param {{ name: string, check_in_at?: string|null, check_out_at?: string|null }} payload
 * @returns {Promise<VoyageDto>}
 */
export async function createVoyage(payload) {
	const response = await httpClient.post("/api/v2/voyages", {
		name: payload.name,
		check_in_at: payload.check_in_at ?? null,
		check_out_at: payload.check_out_at ?? null,
	});
	const body = response.data;
	if (!body?.id) {
		const err = new Error("Brak danych rejsu w odpowiedzi serwera");
		err.response = response;
		throw err;
	}
	console.info('[voyagesApi] createVoyage: utworzono rejs', { id: body?.id, name: body?.name });
	clearCachedVoyagesList();
	return body;
}

/**
 * PUT `/api/v2/voyages/{id}`
 * @param {number|string} id
 * @param {{ name?: string|null, check_in_at?: string|null, check_out_at?: string|null }} payload
 * @returns {Promise<VoyageDto>}
 */
export async function updateVoyage(id, payload) {
	const response = await httpClient.put(
		`/api/v2/voyages/${Number(id)}`,
		buildVoyageBody(payload),
	);
	const body = response.data;
	if (!body?.id) {
		const err = new Error("Brak danych rejsu w odpowiedzi serwera");
		err.response = response;
		throw err;
	}
	console.info('[voyagesApi] updateVoyage: zaktualizowano rejs', { id: body?.id, name: body?.name });
	return body;
}

/**
 * DELETE `/api/v2/voyages/{id}`
 * @param {number|string} id
 */
export async function deleteVoyage(id) {
	const response = await httpClient.delete(`/api/v2/voyages/${Number(id)}`);
	const body = response.data;
	console.info('[voyagesApi] deleteVoyage:', body?.message);
	clearCachedVoyagesList();
}

/** DTO rejsu → stan formularza edycji. */
export function voyageToFormState(voyage) {
	return {
		name: voyage?.name ?? "",
		check_in_at:
			apiDateTimeToDatetimeLocal(voyage?.check_in_at) ||
			voyage?.check_in_at ||
			"",
		check_out_at:
			apiDateTimeToDatetimeLocal(voyage?.check_out_at) ||
			voyage?.check_out_at ||
			"",
	};
}

/** Wyświetlanie dat z API (`YYYY-MM-DD HH:mm:ss` lub ISO). */
export function formatVoyageDateTime(value) {
	if (value == null || value === "") return "—";
	try {
		const normalized = String(value).replace(" ", "T");
		const d = new Date(normalized);
		if (Number.isNaN(d.getTime())) return String(value);
		return d.toLocaleString("pl-PL", {
			year: "numeric",
			month: "2-digit",
			day: "2-digit",
			hour: "2-digit",
			minute: "2-digit",
		});
	} catch {
		return String(value);
	}
}
